(() => {
  'use strict';
  const motionPreference = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealContent = element => {
    if (!motionPreference() && typeof element.animate === 'function') {
      element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
    }
  };
  const emit = (element, phase, message) => element.dispatchEvent(new CustomEvent('biancelle:cart-add', {
    bubbles: true, detail: { phase, message }
  }));

  class BiancelleScentQuiz extends HTMLElement {
    connectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.listeners = new AbortController();
      this.questions = [...this.querySelectorAll('[data-bi-question]')];
      this.flow = this.querySelector('[data-bi-quiz-flow]');
      this.result = this.querySelector('[data-bi-quiz-result]');
      if (!this.questions.length || !this.result || !this.flow) return;
      this.answers = [];
      this.step = 0;
      this.classList.add('is-ready');
      this.addEventListener('click', this.onClick.bind(this), { signal: this.listeners.signal });
      this.showStep(false);
      const url = new URL(window.location.href);
      const shared = url.searchParams.get('stare');
      if (['fresh', 'sweet', 'soft', 'warm'].includes(shared) && url.hash === '#' + this.id) {
        this.showResult(shared, false);
      }
    }

    disconnectedCallback() { this.listeners?.abort(); }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-bi-answer')) {
        if (button.closest('[data-bi-question]') !== this.questions[this.step]) return;
        this.answers[this.step] = button.dataset.biAnswer;
        this.answers.length = this.step + 1;
        if (this.step < this.questions.length - 1) {
          this.step++;
          this.showStep();
        } else this.showResult(this.chooseProfile());
      } else if (button.hasAttribute('data-bi-quiz-back')) {
        this.step = Math.max(0, this.step - 1);
        this.showStep();
      } else if (button.hasAttribute('data-bi-quiz-reset')) {
        this.answers = [];
        this.step = 0;
        this.result.replaceChildren();
        this.showStep();
      } else if (button.hasAttribute('data-bi-quiz-share')) {
        this.share(button);
      }
    }

    showStep(focus = true) {
      this.flow.hidden = false;
      this.result.hidden = true;
      this.questions.forEach((question, index) => {
        question.hidden = index !== this.step;
        question.querySelectorAll('[data-bi-answer]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.biAnswer === this.answers[index]));
        });
      });
      this.querySelector('[data-bi-quiz-count]').textContent = '0' + (this.step + 1) + ' / 03';
      this.querySelector('progress').value = this.step + 1;
      this.querySelector('[data-bi-quiz-back]').disabled = this.step === 0;
      if (focus) revealContent(this.questions[this.step]);
      if (focus) this.questions[this.step].querySelector('legend').focus({ preventScroll: true });
    }

    chooseProfile() {
      const scores = { fresh: 0, sweet: 0, soft: 0, warm: 0 };
      this.answers.forEach((answer, index) => {
        if (Object.hasOwn(scores, answer)) scores[answer] += [2, 1, 2][index];
      });
      // The first preference breaks ties; no unavailable product is substituted.
      const order = [...new Set([this.answers[0], ...Object.keys(scores)])].filter(Boolean);
      return order.reduce((best, key) => scores[key] > scores[best] ? key : best, order[0]);
    }

    showResult(key, focus = true) {
      this.profile = key;
      const template = this.querySelector('template[data-bi-result-key="' + key + '"]');
      this.result.replaceChildren();
      if (template) this.result.append(template.content.cloneNode(true));
      else {
        const title = document.createElement('h3');
        title.dataset.biResultTitle = '';
        title.tabIndex = -1;
        title.textContent = { fresh: 'Luminoasă', sweet: 'Jucăușă', soft: 'Delicată', warm: 'Magnetică' }[key];
        const text = document.createElement('p');
        text.textContent = 'Aceasta este starea sugerată de alegerile tale. Pregătim aromele Biancelle.';
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'bi-x-link';
        reset.dataset.biQuizReset = '';
        reset.textContent = 'Reia quizul';
        this.result.append(title, text, reset);
      }
      this.flow.hidden = true;
      this.result.hidden = false;
      if (focus) revealContent(this.result);
      if (focus) this.result.querySelector('[data-bi-result-title]').focus({ preventScroll: true });
    }

    async share(button) {
      const url = new URL(window.location.href);
      url.searchParams.set('stare', this.profile);
      url.hash = this.id;
      const title = this.result.querySelector('[data-bi-result-title]').textContent.trim();
      const status = this.result.querySelector('[data-bi-share-status]');
      button.disabled = true;
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Starea mea Biancelle', text: 'Astăzi port starea: ' + title + '.', url: url.href });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url.href);
          status.textContent = 'Link copiat. Trimite-l cui vrei.';
        } else status.textContent = 'Copiază acest link: ' + url.href;
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = 'Poți copia acest link: ' + url.href;
      } finally { button.disabled = false; }
    }
  }

  class BiancelleDuo extends HTMLElement {
    connectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.listeners = new AbortController();
      this.form = this.querySelector('form');
      this.selects = [...this.querySelectorAll('select')];
      this.submitButton = this.querySelector('[data-bi-duo-submit]');
      this.status = this.querySelector('[data-bi-duo-status]');
      this.cartLink = this.querySelector('[data-bi-duo-cart]');
      this.total = this.querySelector('[data-bi-duo-total]');
      if (!this.form || this.selects.length !== 2 || !this.submitButton) return;
      this.cartURL = this.dataset.cartUrl || window.routes?.cart_url || '/cart';
      this.money = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: this.dataset.currency || 'RON' });
      this.pending = false;
      this.locked = false;
      this.classList.add('is-ready');
      this.addEventListener('change', () => {
        if (!this.locked) this.status.textContent = '';
        this.sync();
      }, { signal: this.listeners.signal });
      this.form.addEventListener('submit', this.addDuo.bind(this), { signal: this.listeners.signal });
      this.sync();
    }

    disconnectedCallback() { this.listeners?.abort(); }

    selection() {
      return this.selects.map(select => select.selectedOptions[0]).filter(option =>
        option && !option.disabled && /^[1-9]\d*$/.test(option.value) &&
        Number.isSafeInteger(Number(option.value)) &&
        /^\d+$/.test(option.dataset.price || '') &&
        Number.isSafeInteger(Number(option.dataset.price)) && Number(option.dataset.price) >= 0
      );
    }

    sync() {
      const selected = this.selection();
      this.selects.forEach(select => {
        const option = select.selectedOptions[0];
        const slot = select.closest('[data-bi-duo-slot]');
        const image = slot.querySelector('[data-bi-duo-image]');
        const valid = selected.includes(option);
        slot.querySelector('[data-bi-duo-name]').textContent = valid
          ? [option.dataset.productTitle, option.dataset.variantTitle].filter(Boolean).join(' · ')
          : 'Alege ce porți azi.';
        slot.querySelector('[data-bi-duo-price]').textContent = valid ? option.dataset.money : '—';
        if (valid && option.dataset.image) {
          if (image.getAttribute('src') !== option.dataset.image) image.src = option.dataset.image;
          if (image.alt !== option.dataset.productTitle) image.alt = option.dataset.productTitle;
          image.hidden = false;
        } else {
          image.hidden = true;
          image.removeAttribute('src');
        }
        slot.querySelector('[data-bi-duo-empty]').hidden = !image.hidden;
        select.disabled = this.pending;
      });
      this.total.textContent = selected.length === 2
        ? this.money.format(selected.reduce((sum, option) => sum + Number(option.dataset.price), 0) / 100)
        : '—';
      this.submitButton.disabled = selected.length !== 2 || this.pending || this.locked;
      this.submitButton.setAttribute('aria-busy', String(this.pending));
      this.submitButton.textContent = this.pending ? 'Se adaugă…'
        : this.locked ? 'Verifică mai întâi coșul' : 'Adaugă duo-ul în coș +';
    }

    async addDuo(event) {
      event.preventDefault();
      const selected = this.selection();
      if (this.pending || this.locked || selected.length !== 2) return;
      this.pending = true;
      this.status.textContent = '';
      this.cartLink.hidden = true;
      this.sync();
      const quantities = new Map();
      selected.forEach(option => quantities.set(option.value, (quantities.get(option.value) || 0) + 1));
      const payload = { items: [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity })) };
      const drawer = document.querySelector('cart-drawer');
      let sections = [];
      let accepted = false;
      try {
        if (drawer?.getSectionsToRender && drawer?.renderContents) {
          sections = drawer.getSectionsToRender().slice(0, 5);
          payload.sections = sections.map(section => section.id);
          payload.sections_url = window.location.pathname;
          drawer.setActiveElement?.(this.submitButton);
        }
        emit(this, 'start');
        const url = new URL(this.dataset.addUrl || window.routes?.cart_add_url || '/cart/add', window.location.origin);
        url.pathname = url.pathname.replace(/\.js$/, '') + '.js';
        const response = await fetch(url.pathname + url.search, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok || data.status || !Array.isArray(data.items) ||
            !payload.items.every(item => data.items.some(line => Number(line.variant_id || line.id) === item.id))) {
          throw new Error('Unconfirmed cart change');
        }
        accepted = true;
        emit(this, 'success');
        this.status.textContent = 'Duo-ul tău este în coș.';
        this.cartLink.hidden = false;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'biancelle-duo', cartData: data })).catch(() => {});
        }
        // A failed section render must never invite a second purchase submission.
        if (drawer && sections.length && sections.every(section => typeof data.sections?.[section.id] === 'string')) {
          for (const section of sections) {
            const html = new DOMParser().parseFromString(data.sections[section.id], 'text/html');
            if (!html.querySelector(section.selector || '.shopify-section')) throw new Error('Missing cart markup');
          }
          drawer.renderContents({ ...data, id: data.items[0].id });
          drawer.classList.remove('is-empty');
        } else window.location.assign(this.cartURL);
      } catch (error) {
        if (accepted) {
          window.location.assign(this.cartURL);
        } else {
          // An interrupted response or stock error can leave a partial cart change.
          // Do not retry automatically or remove anything the shopper already had.
          this.locked = true;
          this.status.textContent = 'Nu am putut confirma duo-ul complet. Verifică produsele din coș înainte să încerci din nou.';
          this.cartLink.hidden = false;
          emit(this, 'error', this.status.textContent);
        }
      } finally {
        this.pending = false;
        this.sync();
        emit(this, 'complete');
      }
    }
  }

  class BiancelleFilmStrip extends HTMLElement {
    connectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.listeners = new AbortController();
      this.track = this.querySelector('[data-bi-film-track]');
      this.videos = [...this.querySelectorAll('video')];
      if (!this.track) return;
      const signal = this.listeners.signal;
      this.previous = this.querySelector('[data-bi-film-prev]');
      this.next = this.querySelector('[data-bi-film-next]');
      this.arrows = this.querySelector('[data-bi-film-arrows]');
      const scheduleArrows = () => {
        if (this.arrowFrame) return;
        this.arrowFrame = window.requestAnimationFrame(() => {
          this.arrowFrame = null;
          this.updateArrows();
        });
      };
      const move = direction => {
        const card = this.track.firstElementChild;
        this.track.scrollBy({
          left: direction * ((card?.getBoundingClientRect().width || this.track.clientWidth) + 24),
          behavior: motionPreference() ? 'auto' : 'smooth'
        });
      };
      this.previous?.addEventListener('click', () => move(-1), { signal });
      this.next?.addEventListener('click', () => move(1), { signal });
      this.track.addEventListener('scroll', scheduleArrows, { signal, passive: true });
      this.videos.forEach(video => {
        const caption = video.closest('figure')?.querySelector('h3');
        if (caption) video.setAttribute('aria-label', caption.textContent.trim());
        video.addEventListener('play', () => {
          document.querySelectorAll('biancelle-film-strip video').forEach(other => {
            if (other !== video && !other.paused) other.pause();
          });
        }, { signal });
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach(video => video.pause());
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
        }, { threshold: 0.05 });
        this.videos.forEach(video => this.observer.observe(video));
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(scheduleArrows);
        this.resizeObserver.observe(this.track);
      } else window.addEventListener('resize', scheduleArrows, { signal });
      this.updateArrows();
    }

    updateArrows() {
      // Read geometry together, then update only controls whose state changed.
      const width = this.track.clientWidth;
      const total = this.track.scrollWidth;
      const left = this.track.scrollLeft;
      const hidden = total <= width + 2;
      const atStart = left <= 2;
      const atEnd = left + width >= total - 2;
      if (this.arrows && this.arrows.hidden !== hidden) this.arrows.hidden = hidden;
      if (this.previous && this.previous.disabled !== atStart) this.previous.disabled = atStart;
      if (this.next && this.next.disabled !== atEnd) this.next.disabled = atEnd;
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.arrowFrame);
      this.arrowFrame = null;
      this.videos?.forEach(video => video.pause());
    }
  }

  for (const [name, Component] of [
    ['biancelle-scent-quiz', BiancelleScentQuiz],
    ['biancelle-duo', BiancelleDuo],
    ['biancelle-film-strip', BiancelleFilmStrip]
  ]) if (!customElements.get(name)) customElements.define(name, Component);
})();
